import { createSelector } from '@reduxjs/toolkit';

import { ComponentDesignTimestamps } from '@paragon/contract-library';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { RootState } from '..';
import { createSimpleSetSelector } from './util/createSimpleSetSelector';
import { areWorkingChangesByComponentDesignGuidMap } from './workingChangesSelectors';

export type ComponentDesignAnalysisStatus = 'unanalyzed' | 'checkExceeded' | 'hasErrors' | 'passing' | null;

export function getComponentDesignAnalysisStatus(
  timestamps: ComponentDesignTimestamps | undefined,
  hasWorkingChanges: boolean,
): ComponentDesignAnalysisStatus {
  if (hasWorkingChanges || timestamps?.lastAnalyzed == null) return 'unanalyzed';
  if (timestamps.lastAnalyzedCapacity != null && timestamps.lastAnalyzedCapacity > 1) return 'checkExceeded';
  if (timestamps.lastAnalysisHadErrors) return 'hasErrors';
  if (timestamps.lastAnalyzedCapacity != null) return 'passing';
  return null;
}

const getAnalysisStatusByComponentDesignGuid = createSelector(
  (state: RootState) => state.componentDesignTimestamps,
  areWorkingChangesByComponentDesignGuidMap,

  (timestampsByGuid, workingChangesByGuid) =>
    Object.fromEntries(
      Object.keys(workingChangesByGuid).map((guid) => [
        guid,
        getComponentDesignAnalysisStatus(timestampsByGuid[guid], workingChangesByGuid[guid] ?? false),
      ]),
    ),
);

function createStatusGuidSetSelector(matches: (status: ComponentDesignAnalysisStatus) => boolean) {
  return createSimpleSetSelector(
    getAnalysisStatusByComponentDesignGuid,
    (statusByGuid): Set<string> => new Set(Object.keys(statusByGuid).filter((guid) => matches(statusByGuid[guid]))),
  );
}

function getStatusBuckets(status: ComponentDesignAnalysisStatus): {
  analyzed: boolean;
  passing: boolean;
  failing: boolean;
} {
  switch (status) {
    case 'unanalyzed':
      return { analyzed: false, passing: false, failing: false };
    case 'checkExceeded':
    case 'hasErrors':
      return { analyzed: true, passing: false, failing: true };
    case 'passing':
      return { analyzed: true, passing: true, failing: false };
    case null:
      return { analyzed: true, passing: false, failing: false };
    default:
      assertExhaustiveSwitch(status);
  }
}

export const getAnalyzedComponentDesignGuids = createStatusGuidSetSelector(
  (status) => getStatusBuckets(status).analyzed,
);
export const getPassingComponentDesignGuids = createStatusGuidSetSelector((status) => getStatusBuckets(status).passing);
export const getFailingComponentDesignGuids = createStatusGuidSetSelector((status) => getStatusBuckets(status).failing);
